import Icon from "@ant-design/icons";
import AiAnalysisIcon from "@images/icons/icon-ai-analysis.svg?react";
import { withAuthentication } from "admin/auth/authentication_modal";
import { createExplorational } from "admin/rest_api";
import { Alert, Button, Dropdown, Modal, Popover, Space } from "antd";
import { AsyncButton, type AsyncButtonProps } from "components/async_clickables";
import { NewVolumeLayerSelection } from "dashboard/advanced_dataset/create_explorative_modal";
import { useWkSelector } from "libs/react_hooks";
import { isUserAdminOrManager } from "libs/utils";
import { ArbitraryVectorInput } from "libs/vector_input";
import type React from "react";
import { Fragment, PureComponent, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { UserThemeConfigProvider } from "theme_provider";
import type { APIDataset, APIOrganization, APISegmentationLayer, APIUser } from "types/api_types";
import { type AdditionalCoordinate, APIJobCommand } from "types/api_types";
import constants, {
  type ControlMode,
  ControlModeEnum,
  MappingStatusEnum,
  type ViewMode,
} from "viewer/constants";
import { mayEditAnnotation } from "viewer/model/accessors/annotation_accessor";
import {
  doesSupportVolumeWithFallback,
  getColorLayers,
  getMappingInfoForSupportedLayer,
  getSegmentationLayers,
  getUnifiedAdditionalCoordinates,
  getVisibleSegmentationLayers,
  is2dDataset,
} from "viewer/model/accessors/dataset_accessor";
import { setAdditionalCoordinatesAction } from "viewer/model/actions/flycam_actions";
import { setAIJobDrawerStateAction } from "viewer/model/actions/ui_actions";
import type { WebknossosState } from "viewer/store";
import Store from "viewer/store";
import AddNewLayoutModal from "viewer/view/action_bar/add_new_layout_modal";
import DatasetPositionAndRotationView from "viewer/view/action_bar/dataset_position_view";
import ToolbarView from "viewer/view/action_bar/tools/toolbar_view";
import TracingActionsView, {
  getLayoutMenu,
  type LayoutProps,
} from "viewer/view/action_bar/tracing_actions_view";
import ViewDatasetActionsView from "viewer/view/action_bar/view_dataset_actions_view";
import {
  addNewLayout,
  deleteLayout,
  getLayoutConfig,
  LayoutEvents,
  layoutEmitter,
} from "viewer/view/layouting/layout_persistence";
import { ACTIONBAR_MARGIN_LEFT } from "./action_bar/tools/tool_helpers";
import ToolkitView from "./action_bar/tools/toolkit_switcher_view";
import NumberSliderSetting from "./left_border_tabs/components/number_slider_setting";

const ButtonWithAuthentication = withAuthentication<AsyncButtonProps, typeof AsyncButton>(
  AsyncButton,
);

const VersionRestoreWarning = (
  <Alert
    title="Read-only version restore mode active!"
    style={{
      padding: "4px 15px",
    }}
    type="info"
  />
);
type StateProps = {
  dataset: APIDataset;
  activeUser: APIUser | null | undefined;
  activeOrganization: APIOrganization | null;
  controlMode: ControlMode;
  showVersionRestore: boolean;
  is2d: boolean;
  viewMode: ViewMode;
};
type OwnProps = {
  layoutProps: LayoutProps;
};
type Props = OwnProps & StateProps;
type State = {
  isNewLayoutModalOpen: boolean;
  windowWidth: number;
};

const ADDITIONAL_COORDINATE_VALUE_SPAN = 4;

function AdditionalCoordinatesInputView() {
  const additionalAxes = useWkSelector((state) => getUnifiedAdditionalCoordinates(state.dataset));
  const additionalCoordinates = useWkSelector((state) => state.flycam.additionalCoordinates);
  const dispatch = useDispatch();
  const changeAdditionalCoordinates = (values: AdditionalCoordinate[] | null) => {
    if (values != null) {
      dispatch(setAdditionalCoordinatesAction(values));
    }
  };
  const changeAdditionalCoordinatesFromVector = (values: number[]) => {
    if (additionalCoordinates != null) {
      dispatch(
        setAdditionalCoordinatesAction(
          additionalCoordinates.map((el, index) => ({
            ...el,
            value: values[index],
          })),
        ),
      );
    }
  };

  if (additionalCoordinates == null || additionalCoordinates.length === 0) {
    return null;
  }
  // The popover shrink-wraps its content and NumberSliderSetting sizes its columns in
  // percentages, so without an explicit width the value column only fits about two digits.
  // Size the content so that column fits the longest bound (plus the input's padding).
  const maxValueLength = Math.max(
    ...additionalCoordinates.map((coord) => {
      const { bounds } = additionalAxes[coord.name];
      return Math.max(String(bounds[0]).length, String(bounds[1] - 1).length);
    }),
  );
  const contentWidth = `calc(${24 / ADDITIONAL_COORDINATE_VALUE_SPAN} * (${maxValueLength}ch + 20px))`;
  return (
    <Popover
      content={
        <div style={{ width: contentWidth, minWidth: 250 }}>
          {additionalCoordinates.map((coord, idx) => {
            const { bounds } = additionalAxes[coord.name];
            return (
              <NumberSliderSetting
                label={coord.name}
                key={coord.name}
                min={bounds[0]}
                max={bounds[1] - 1}
                value={coord.value}
                spans={[
                  2,
                  24 - 2 - ADDITIONAL_COORDINATE_VALUE_SPAN,
                  ADDITIONAL_COORDINATE_VALUE_SPAN,
                ]}
                onChange={(newCoord) => {
                  const newCoords = additionalCoordinates.slice();
                  newCoords[idx] = {
                    ...newCoords[idx],
                    value: newCoord,
                  };
                  changeAdditionalCoordinates(newCoords);
                }}
                wheelFactor={0.05}
              />
            );
          })}
        </div>
      }
      placement="bottom"
    >
      {/* this div is needed to prevent the popover from being offset to the top-left screen */}
      <div>
        <ArbitraryVectorInput
          vectorLength={additionalCoordinates.length}
          value={additionalCoordinates.map((el) => el.value)}
          onChange={changeAdditionalCoordinatesFromVector}
          style={{ marginLeft: ACTIONBAR_MARGIN_LEFT }}
          vectorLabel={additionalCoordinates.map((coord) => coord.name).join("")}
        />
      </div>
    </Popover>
  );
}

function CreateAnnotationButton() {
  const navigate = useNavigate();
  const activeUser = useWkSelector((state) => state.activeUser);
  const visibleSegmentationLayers = useWkSelector((state) => getVisibleSegmentationLayers(state));
  const segmentationLayers = useWkSelector((state) => getSegmentationLayers(state.dataset));
  const dataset = useWkSelector((state) => state.dataset);
  const [isLayerSelectionModalVisible, setLayerSelectionModalVisible] = useState<boolean>(false);
  const [selectedLayerName, setSelectedLayerName] = useState<string | undefined>(undefined);

  const getUnambiguousSegmentationLayer = () => {
    if (visibleSegmentationLayers?.length === 1) return visibleSegmentationLayers[0];
    if (segmentationLayers.length === 1) return segmentationLayers[0];
    return null;
  };

  const continueWithLayer = async (layer: APISegmentationLayer | null | undefined) => {
    // If the dataset supports creating an annotation with a fallback segmentation,
    // use it (as the fallback can always be removed later)
    const fallbackLayerName = getFallbackLayerName(layer);
    const mappingInfo = getMappingInfoForSupportedLayer(Store.getState());
    let maybeMappingName = null;
    if (
      mappingInfo.mappingStatus !== MappingStatusEnum.DISABLED &&
      mappingInfo.mappingType !== "JSON"
    ) {
      maybeMappingName = mappingInfo.mappingName;
    }

    const annotation = await createExplorational(
      dataset.id,
      "hybrid",
      false,
      fallbackLayerName,
      maybeMappingName,
    );
    navigate(`/annotations/${annotation.id}${location.hash}`);
  };

  const getFallbackLayerName = (segmentationLayer: APISegmentationLayer | null | undefined) => {
    return segmentationLayer && doesSupportVolumeWithFallback(dataset, segmentationLayer)
      ? segmentationLayer.name
      : null;
  };

  const onClick = async () => {
    // This will be set in cases where it is clear which layer to use.
    const unambiguousSegmentationLayer = getUnambiguousSegmentationLayer();
    if (unambiguousSegmentationLayer == null && segmentationLayers.length > 1) {
      setLayerSelectionModalVisible(true);
      return;
    }
    await continueWithLayer(unambiguousSegmentationLayer);
  };

  const handleLayerSelected = async () => {
    setLayerSelectionModalVisible(false);
    const selectedLayer = selectedLayerName
      ? segmentationLayers.find((layer) => layer.name === selectedLayerName)
      : null;
    await continueWithLayer(selectedLayer);
  };

  return (
    <div
      onKeyDownCapture={(e: React.KeyboardEvent) => {
        // Prevent closing the modal upon pressing some keys.
        if (e.ctrlKey || e.metaKey || e.key === "AltGraph" || e.getModifierState("AltGraph")) {
          e.stopPropagation();
        }
      }}
    >
      <ButtonWithAuthentication
        activeUser={activeUser}
        authenticationMessage="You have to register or login to create an annotation."
        type="primary"
        onClick={onClick}
      >
        Create Annotation
      </ButtonWithAuthentication>

      <UserThemeConfigProvider>
        <Modal
          title="Select Segmentation Layer"
          open={isLayerSelectionModalVisible}
          onCancel={() => setLayerSelectionModalVisible(false)}
          onOk={handleLayerSelected}
        >
          <NewVolumeLayerSelection
            segmentationLayers={segmentationLayers}
            dataset={dataset}
            selectedSegmentationLayerName={selectedLayerName}
            setSelectedSegmentationLayerName={setSelectedLayerName}
          />
        </Modal>
      </UserThemeConfigProvider>
    </div>
  );
}

function ModesView() {
  const controlMode = useWkSelector((state) => state.temporaryConfiguration.controlMode);
  const isViewMode = controlMode === ControlModeEnum.VIEW;
  const isReadOnly = useWkSelector((state) => !mayEditAnnotation(state));
  const isOrthoMode = useWkSelector(
    (state) => state.temporaryConfiguration.viewMode === "orthogonal",
  );

  // The outer div is necessary for proper spacing.
  return isViewMode || isReadOnly || !isOrthoMode ? null : (
    <div>
      <Space.Compact>
        <ToolkitView />
      </Space.Compact>
    </div>
  );
}

class ActionBarView extends PureComponent<Props, State> {
  state: State = {
    isNewLayoutModalOpen: false,
    windowWidth: window.innerWidth,
  };

  handleResize = () => {
    this.setState({ windowWidth: window.innerWidth });
  };

  componentDidMount() {
    window.addEventListener("resize", this.handleResize);
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.handleResize);
  }

  handleResetLayout = () => {
    layoutEmitter.emit(
      LayoutEvents.resetLayout,
      this.props.layoutProps.layoutKey,
      this.props.layoutProps.activeLayout,
    );
  };

  handleLayoutDeleted = (layoutName: string) => {
    deleteLayout(this.props.layoutProps.layoutKey, layoutName);
  };

  addNewLayout = (layoutName: string) => {
    this.setState({
      isNewLayoutModalOpen: false,
    });
    const configForLayout = getLayoutConfig(
      this.props.layoutProps.layoutKey,
      this.props.layoutProps.activeLayout,
    );

    if (addNewLayout(this.props.layoutProps.layoutKey, layoutName, configForLayout)) {
      this.props.layoutProps.setCurrentLayout(layoutName);
    }
  };

  renderStartAIJobButton(disabled: boolean, tooltipTextIfDisabled: string): React.ReactNode {
    const tooltipText = disabled ? tooltipTextIfDisabled : "Start a processing job using AI";

    const menuItems = [
      {
        key: "open_ai_inference_button",
        onClick: () => Store.dispatch(setAIJobDrawerStateAction("open_ai_inference")),
        label: "Run AI model",
      },
      {
        key: "open_ai_training_button",
        onClick: () => Store.dispatch(setAIJobDrawerStateAction("open_ai_training")),
        label: "Train new AI model",
      },
      {
        key: "open_ai_alignment_button",
        onClick: () => Store.dispatch(setAIJobDrawerStateAction("open_ai_alignment")),
        label: "Run AI Alignment",
      },
    ];

    let buttonText = "AI Analysis";
    if (this.state.windowWidth < constants.NARROW_SCREEN_WIDTH) {
      buttonText = "AI";
    }
    if (this.state.windowWidth < constants.VERY_NARROW_SCREEN_WIDTH) {
      buttonText = "";
    }

    return (
      // div is for left spacing through CSS
      <div>
        <Dropdown
          key="ai-job-dropdown"
          menu={{
            items: menuItems,
          }}
          disabled={disabled}
        >
          <Button
            disabled={disabled}
            icon={<Icon component={AiAnalysisIcon} />}
            title={tooltipText}
            type={
              this.state.windowWidth < constants.VERY_NARROW_SCREEN_WIDTH ? "primary" : "default"
            }
          >
            {buttonText}
          </Button>
        </Dropdown>
      </div>
    );
  }

  renderStartTracingButton(): React.ReactNode {
    return <CreateAnnotationButton />;
  }

  render() {
    const { dataset, is2d, showVersionRestore, controlMode, layoutProps, viewMode, activeUser } =
      this.props;
    const isAdminOrManager = isUserAdminOrManager(activeUser);
    const isViewMode = controlMode === ControlModeEnum.VIEW;
    const getIsAIAnalysisEnabled = () => {
      const jobsEnabled =
        dataset.dataStore.jobsSupportedByAvailableWorkers.includes(APIJobCommand.INFER_NEURONS) ||
        dataset.dataStore.jobsSupportedByAvailableWorkers.includes(
          APIJobCommand.INFER_MITOCHONDRIA,
        ) ||
        dataset.dataStore.jobsSupportedByAvailableWorkers.includes(APIJobCommand.INFER_INSTANCES) ||
        dataset.dataStore.jobsSupportedByAvailableWorkers.includes(APIJobCommand.ALIGN_SECTIONS);
      return jobsEnabled;
    };

    const layoutMenu = getLayoutMenu({
      ...layoutProps,
      addNewLayout: () => {
        this.setState({
          isNewLayoutModalOpen: true,
        });
      },
      onResetLayout: this.handleResetLayout,
      onSelectLayout: layoutProps.setCurrentLayout,
      onDeleteLayout: this.handleLayoutDeleted,
    });

    const colorLayers = getColorLayers(dataset);
    const datasetHasNoColorLayer = colorLayers.length === 0;
    const isNd = (colorLayers[0]?.additionalAxes ?? []).length > 0;
    const is2DOrNDDataset = isNd || is2d;
    const isAIAnalysisDisabled = !getIsAIAnalysisEnabled();
    const shouldDisableAIJobButton =
      isAIAnalysisDisabled || datasetHasNoColorLayer || is2DOrNDDataset;
    let tooltip = "AI analysis is not enabled for this dataset.";
    if (datasetHasNoColorLayer) {
      tooltip = "The dataset needs to have a color layer to start AI processing jobs.";
    } else if (is2DOrNDDataset) {
      tooltip = `AI Analysis is not supported for ${is2d ? "2D" : "ND"} datasets.`;
    }

    return (
      <Fragment>
        <div className="action-bar">
          {isViewMode || showVersionRestore ? (
            <ViewDatasetActionsView layoutMenu={layoutMenu} />
          ) : (
            <TracingActionsView layoutMenu={layoutMenu} />
          )}
          {showVersionRestore ? VersionRestoreWarning : null}
          <DatasetPositionAndRotationView />
          <AdditionalCoordinatesInputView />
          {getIsAIAnalysisEnabled() && isAdminOrManager
            ? this.renderStartAIJobButton(shouldDisableAIJobButton, tooltip)
            : null}
          {isViewMode ? this.renderStartTracingButton() : null}
          <ModesView />
          {constants.MODES_PLANE.indexOf(viewMode) > -1 ? <ToolbarView /> : null}
        </div>
        <UserThemeConfigProvider>
          <AddNewLayoutModal
            addLayout={this.addNewLayout}
            isOpen={this.state.isNewLayoutModalOpen}
            onCancel={() =>
              this.setState({
                isNewLayoutModalOpen: false,
              })
            }
          />
        </UserThemeConfigProvider>
      </Fragment>
    );
  }
}

const mapStateToProps = (state: WebknossosState): StateProps => ({
  dataset: state.dataset,
  activeUser: state.activeUser,
  activeOrganization: state.activeOrganization,
  controlMode: state.temporaryConfiguration.controlMode,
  showVersionRestore: state.uiInformation.showVersionRestore,
  is2d: is2dDataset(state.dataset),
  viewMode: state.temporaryConfiguration.viewMode,
});

const connector = connect(mapStateToProps);
export default connector(ActionBarView);
